import { z } from 'zod';
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile';
import { REQUESTER_TYPES } from '../types/customers.types';

const INVALID_EMAIL_MESSAGE = 'Enter an email address in the correct format, like name@example.com';
const MOBILE_REQUIRED_MESSAGE = 'Mobile phone is required';
const MOBILE_INVALID_MESSAGE = 'Enter a valid mobile number, like 07123 456789';
const MOBILE_MAX_MESSAGE = 'Mobile phone number cannot exceed 20 characters';
const MOBILE_MAX_LENGTH = 20;

const MOBILE_TYPES = new Set(['MOBILE', 'FIXED_LINE_OR_MOBILE']);

// `GB` is the region national-format input is parsed against, not a country restriction; the
// number TYPE is what makes this the mobile field. Mirrors `ValidPhoneNumber::mobile()`.
const isMobileNumber = (value: string): boolean => {
    const parsed = parsePhoneNumberFromString(value, 'GB');
    return parsed !== undefined && parsed.isValid() && MOBILE_TYPES.has(parsed.getType() ?? '');
};

const mobilePhoneField = z
    .string({ required_error: MOBILE_REQUIRED_MESSAGE })
    .trim()
    .min(1, MOBILE_REQUIRED_MESSAGE)
    .max(MOBILE_MAX_LENGTH, MOBILE_MAX_MESSAGE)
    .refine(isMobileNumber, MOBILE_INVALID_MESSAGE);

export const newCustomerSchema = z.object({
    title: z.string({ required_error: 'Title is required' }).min(1, 'Title is required'),
    forename: z.string({ required_error: 'Forename is required' }).min(1, 'Forename is required'),
    surname: z.string({ required_error: 'Surname is required' }).min(1, 'Surname is required'),
    addressLine1: z
        .string({ required_error: 'Address Line 1 is required' })
        .min(1, 'Address Line 1 is required'),
    addressLine2: z.string().optional(),
    city: z.string({ required_error: 'City is required' }).min(1, 'City is required'),
    postcode: z.string({ required_error: 'Postcode is required' }).min(1, 'Postcode is required'),
    email: z
        .string({ required_error: 'Email is required' })
        .min(1, 'Email is required')
        .email(INVALID_EMAIL_MESSAGE),
    mobilePhone: mobilePhoneField,
    businessName: z.string().optional(),
    vatNumber: z.string().optional(),
});

export type NewCustomerFormInputs = z.infer<typeof newCustomerSchema>;

// Title/forename/surname are intentionally excluded — the name change is Epic 3.7
// slice C1b and has its own permission, reason and evidence.
export const editableContactFieldsSchema = z.object({
    addressLine1: z.string().min(1, 'Address Line 1 is required').max(255),
    addressLine2: z.string().max(255).optional(),
    city: z.string().min(1, 'City is required').max(100),
    postcode: z.string().min(1, 'Postcode is required').max(20),
    email: z.string().email(INVALID_EMAIL_MESSAGE).max(255).optional(),
    mobilePhone: mobilePhoneField,
    businessName: z.string().max(255).optional(),
    vatNumber: z.string().max(50).optional(),
});

// Mode B (pending customer, no `customer.id`) — all fields editable, including
// name/title, since there is no canonical record and no Agreement referencing
// them yet. Mirrors newCustomerSchema's required title/forename/surname.
export const editablePendingCustomerSchema = editableContactFieldsSchema.extend({
    title: z.string({ required_error: 'Title is required' }).min(1, 'Title is required'),
    forename: z.string({ required_error: 'Forename is required' }).min(1, 'Forename is required'),
    surname: z.string({ required_error: 'Surname is required' }).min(1, 'Surname is required'),
});

export type EditCustomerFormInputs = z.infer<typeof editableContactFieldsSchema>;
export type EditPendingCustomerFormInputs = z.infer<typeof editablePendingCustomerSchema>;

// Epic 3.7 slice C1a — the holder contact/address/company change made from an
// agreement. Mirrors UpdateCustomerRequest field for field.
export const holderContactDetailsSchema = editableContactFieldsSchema.extend({
    requesterType: z.enum(REQUESTER_TYPES).optional(),
});

export type HolderContactDetailsFormInputs = z.infer<typeof holderContactDetailsSchema>;
